/**
 * Background layers — a `background` node can stack several images, each with
 * its own size/alignment. Shared by the guest renderer, the builder canvas and
 * the inspector so all three build the exact same CSS from the same data.
 *
 * Mirrors backend/utils/landingPageSchema.js's layerSchema (the real
 * security boundary — it restricts `src` to a site path / http(s) URL and
 * clamps x/y to 0–100):
 *   x / y  — background-position as a percentage (0 = left/top, 100 = right/bottom)
 *   size   — 'cover' | 'contain' | 'auto' (the image's natural size)
 *
 * The FIRST layer in the array is the TOP-most image (CSS paints the first
 * background-image listed on top), so the inspector's "Layer 1" is what the
 * guest sees in front.
 */

export const MAX_BG_LAYERS = 6;
export const BG_SIZES = ['cover', 'contain', 'auto'];

export function newLayer(src) {
    return { src, x: 50, y: 50, size: 'cover', repeat: false };
}

/**
 * The layers a background node actually renders. Pages saved before layers
 * existed carry a single `props.src` — that keeps working, treated as one
 * centred "cover" layer, exactly how it always rendered.
 */
export function getBackgroundLayers(node) {
    if (Array.isArray(node?.layers) && node.layers.length > 0) return node.layers;
    if (node?.props?.src) return [newLayer(node.props.src)];
    return [];
}

// src is already restricted server-side to a path/URL with no quotes,
// parentheses, whitespace or backslashes; encoding again here means a value
// that somehow got past that still can't break out of the CSS url("...").
export function cssUrl(src) {
    // Explicit percent-encoding: encodeURIComponent() would leave ( ) untouched.
    const encode = (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`;
    return `url("${String(src).replace(/["\\\n\r()]/g, encode)}")`;
}

/** Inline-style object for a set of layers, or {} when there are none. */
export function backgroundStyleFor(layers) {
    if (!layers || layers.length === 0) return {};
    return {
        backgroundImage: layers.map((l) => cssUrl(l.src)).join(', '),
        backgroundSize: layers.map((l) => l.size || 'cover').join(', '),
        backgroundPosition: layers.map((l) => `${l.x ?? 50}% ${l.y ?? 50}%`).join(', '),
        backgroundRepeat: layers.map((l) => (l.repeat ? 'repeat' : 'no-repeat')).join(', '),
    };
}
